import {
  LayerGeometryType,
  LayerMetadata,
  DataSetTheme,
} from "@accenture/energy-transition-interfaces";
import React, { useEffect, useState } from "react";
import { Layer, LayerProps, Source, SourceProps, useMap } from "react-map-gl";
import {
  selectIsLayerActive,
  selectSelectedFeatures,
} from "../../redux/mapSlice";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { getBeforeIdByLayerType } from "../../utils/utils";

declare const AWS_API_URI: string;

const highwayLayerMetadata: LayerMetadata = {
  layerName: "Highway",
  sourceID: "highways-layer-source",
  layerID: "highway-layer",
  dataset: "state_highways",
  toggleDotColor: "#4C516D",
  description: "This dataset displays the location of highways.",
  fieldsToDisplay: {
    FULLNAME: "Full Name",
  },
  theme: [DataSetTheme.Transportation],
};

export const HighwayLayer = () => {
  const { map: map } = useMap();
  const [hoveredFeatureID, setHoveredFeatureID] = useState<string>("");
  const selectedFeatures = useSelector((state: RootState) =>
    selectSelectedFeatures(state, highwayLayerMetadata.layerID),
  );
  const showLayer = useSelector((state: RootState) =>
    selectIsLayerActive(state, highwayLayerMetadata.layerID),
  );

  useEffect(() => {
    if (!map) return;
    map.on("mousemove", highwayLayerMetadata.layerID, (e) => {
      if (e === undefined || e.features === undefined) return;
      if (e.features.length > 0 && e.features[0] !== null) {
        map.getCanvas().style.cursor = "pointer";
        setHoveredFeatureID(e?.features[0]?.properties?.LINEARID);
      }
    });
    map.on("mouseleave", highwayLayerMetadata.layerID, () => {
      map.getCanvas().style.cursor = "";
      setHoveredFeatureID("");
    });
  }, []);

  const sourceConfig: SourceProps = {
    id: highwayLayerMetadata.sourceID,
    type: "vector",
    tiles: [
      `${AWS_API_URI}/tiles/${highwayLayerMetadata.dataset}/{z}/{x}/{y}.mvt`,
    ],
  };

  const layerConfig: LayerProps = {
    id: highwayLayerMetadata.layerID,
    source: highwayLayerMetadata.sourceID,
    "source-layer": highwayLayerMetadata.dataset,
    beforeId: getBeforeIdByLayerType(LayerGeometryType.Fill),
    type: "line",
    paint: {
      "line-color": highwayLayerMetadata.toggleDotColor,
      "line-width": [
        "case",
        [
          "any",
          ["==", ["get", "LINEARID"], hoveredFeatureID],
          [
            "in",
            ["get", "LINEARID"],
            ["literal", selectedFeatures.map((f) => f.properties.LINEARID)],
          ],
        ],
        3,
        1,
      ],
    },
    layout: {
      visibility: showLayer ? "visible" : "none",
    },
  };

  return (
    <Source {...sourceConfig}>
      <Layer {...layerConfig} />
    </Source>
  );
};
HighwayLayer.metadata = highwayLayerMetadata;
